import { I18n } from '@boschebike/ebds-js';
import { useContext } from 'react';
import {
  HeaderProps, InternalEventLink, InternalNavigationLink, PageTabless,
} from '../../components';
import { DxfIntegrationContext } from '../../context/dxfIntegrationContext';
import {
  addDomainToRelativeLink,
  getMBPSPortalLink,
  interpolateContentToJSX,
} from '../../helpers';
import { onScroll } from '../../helpers/scroll';
import { useContent } from '../../hooks/use-content';
import { LocalizationContext } from '../../context/localizationContext';

interface DataProtectionPolicyPageProps {
  page?: string;
  langCode?: string;
}
export const DataProtectionPolicyPage = ({
  page = 'portal.data.protectionPolicy',
  langCode,
}: DataProtectionPolicyPageProps) => {
  const content: I18n = useContent(langCode);
  const localizationContext = useContext(LocalizationContext);
  const {
    termsOfUseLink,
    termsOfUseForSubUsersLink,
    skidTermsOfUseLink,
    contentCardCreatorLink,
  } = useContext(DxfIntegrationContext);

  if (localizationContext.locale === 'en-TW') {
    // eslint-disable-next-line no-param-reassign
    page = 'portal.data.protectionPolicy.tw';
  }

  const contentKeyPage: string = content.t(page) + content.t(`${page}.chunk2`);
  const contentKeyMBPSPortalLink: string = content.t('portal.dataProtection.link.mbpsPortalLink');
  const contentKeyContentCardCreatorLink: string = content.t('portal.dataProtection.link.contentCardCreatorLink');
  const contentKeyContactScroller: string = content.t('portal.dataProtection.link.contact');
  const contentKeyUsCaContentScroller: string = content.t('portal.dataProtection.link.usCaContentLink');
  const contentKeyProcessingPurposesAndLegalBasisScroller: string = content.t('portal.dataProtection.link.processingPurposesAndLegalBasis');

  const jsxContent: JSX.Element[] = interpolateContentToJSX(contentKeyPage, [
    {
      selector: 'us-ca-data-protection-notice-scroller',
      element: InternalEventLink,
      elementProps: {
        onClickEvent: onScroll(contentKeyPage, 'us-ca-data-protection-notice'),
        text: contentKeyUsCaContentScroller,
      },
    },
    {
      selector: 'terms-of-use-link',
      element: InternalNavigationLink,
      elementProps: {
        href: termsOfUseLink,
        text: addDomainToRelativeLink(termsOfUseLink),
      },
    },
    {
      selector: 'content-card-creator-link',
      element: InternalNavigationLink,
      elementProps: {
        href: contentCardCreatorLink,
        text: contentKeyContentCardCreatorLink,
      },
    },
    {
      selector: 'contact-scroller',
      element: InternalEventLink,
      elementProps: {
        onClickEvent: onScroll(contentKeyPage, '17-contact'),
        text: contentKeyContactScroller,
      },
    },
    {
      selector: 'processing-purposes-and-legal-basis-scroller',
      element: InternalEventLink,
      elementProps: {
        onClickEvent: onScroll(contentKeyPage, '5-processing-purposes-and-legal-basis'),
        text: contentKeyProcessingPurposesAndLegalBasisScroller,
      },
    },
    {
      selector: 'singlekey-id-terms-of-use',
      element: InternalNavigationLink,
      elementProps: {
        href: skidTermsOfUseLink,
        text: skidTermsOfUseLink,
      },
    },
    {
      selector: 'terms-of-use-for-sub-users-link',
      element: InternalNavigationLink,
      elementProps: {
        href: termsOfUseForSubUsersLink,
        text: addDomainToRelativeLink(termsOfUseForSubUsersLink),
      },
    },
    {
      selector: 'mbps-portal-link',
      element: InternalNavigationLink,
      elementProps: {
        href: getMBPSPortalLink(),
        text: contentKeyMBPSPortalLink,
      },
    },
  ]);

  const header: HeaderProps = {
    title: content.t('portal.dataProtection.headline'),
    headline: content.t('portal.dataProtection.subheadline'),
    lightWeight: true,
  };

  return (
    <PageTabless headerProps={header}>{...jsxContent}</PageTabless>
  );
};
